import { gql } from './graphql';

/**
 * Campus = a group of facilities. Users read and move within their whole campus but
 * dock only at their own facilities (`canDock`); the server enforces this.
 */

export interface CampusFacility {
  id: string;
  name: string;
  facility_alias?: string | null;
  configuration?: any;
}

export interface Campus {
  uuid: string;
  name: string;
  facilities: CampusFacility[];
}

const CAMPUS_BY_FACILITY = `
  query CampusByFacility($facilityId: ID!) {
    campus_by_facility(facility_id: $facilityId) {
      uuid
      name
      facilities {
        id
        name
        facility_alias
        configuration
      }
    }
  }
`;

/** The campus a facility belongs to, or null for a facility with no campus. */
export async function fetchCampusByFacility(facilityId: string): Promise<Campus | null> {
  const data = await gql<{ campus_by_facility: Campus | null }>(CAMPUS_BY_FACILITY, {
    facilityId
  });
  return data.campus_by_facility;
}

/** One campus facility's zones, spots and docks, with occupancy (`trailer_uuid`). */
export interface CampusDestinationFacility {
  facility_id: string;
  facility_name: string;
  facility_alias?: string | null;
  facility_configuration?: any;
  is_anchor: boolean;
  can_dock: boolean;
  facility_zones?: Array<{
    uuid: string;
    name: string;
    zone_type?: string;
    configuration?: any;
  }>;
  yard_spots?: Array<{
    uuid: string;
    name: string;
    facility_zone_uuid: string;
    order_number?: string | null;
    configuration?: any;
    trailer_uuid?: string | null;
  }>;
  warehouse_zones?: Array<{ uuid: string; name: string; configuration?: any }>;
  docks?: Array<{
    uuid: string;
    name: string;
    warehouse_zone_uuid: string;
    order_number?: string | null;
    configuration?: any;
    trailer_uuid?: string | null;
  }>;
}

export interface FacilityLock {
  resource_uuid?: string;
  model_name?: string;
  facility_id?: string;
  task_uuid?: string;
  resource?: { __typename?: string; uuid?: string } | null;
}

const CAMPUS_DESTINATIONS = `
  query CampusDestinations($facilityId: ID!) {
    campus_destinations(facility_id: $facilityId) {
      facility_id
      facility_name
      facility_alias
      facility_configuration
      is_anchor
      can_dock
      facility_zones { uuid name zone_type configuration }
      yard_spots { uuid name facility_zone_uuid order_number configuration trailer_uuid }
      warehouse_zones { uuid name configuration }
      docks { uuid name warehouse_zone_uuid order_number configuration trailer_uuid }
    }
    facilityLocks(facilityId: $facilityId) {
      resource_uuid
      model_name
      facility_id
      task_uuid
      resource {
        __typename
        ... on YardSpotObject {
          uuid
        }
        ... on DockObject {
          uuid
        }
        ... on Trailer {
          uuid
        }
      }
    }
  }
`;

/** Every move destination in the campus of `facilityId`, plus the campus-wide locks. */
export async function fetchCampusDestinations(
  facilityId: string
): Promise<{ facilities: CampusDestinationFacility[]; locks: FacilityLock[] }> {
  const data = await gql<{
    campus_destinations: CampusDestinationFacility[] | null;
    facilityLocks: FacilityLock[] | null;
  }>(CAMPUS_DESTINATIONS, { facilityId });

  return { facilities: data.campus_destinations || [], locks: data.facilityLocks || [] };
}
